import React from 'react';
import { HashRouter as Router } from 'react-router-dom';
import { usePasswordAccess } from './hooks/usePasswordAccess';
import { useSharedSupabaseData } from './hooks/useSharedSupabaseData';
import { useSharedImageData } from './hooks/useSharedImageData';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import PasswordForm from './components/PasswordForm';
import SharedHeader from './components/SharedHeader';
import VideoSection from './components/VideoSection';
import NewVisualizationSection from './components/NewVisualizationSection';
import NewGratitudeSection from './components/NewGratitudeSection';
import NewAvoidanceSection from './components/NewAvoidanceSection';
import NewInjusticeSection from './components/NewInjusticeSection';
import NewMilestoneSection from './components/NewMilestoneSection';
import GoalsSection from './components/GoalsSection';
import RealMotivesSection from './components/RealMotivesSection';
import DailyNotesSection from './components/DailyNotesSection';

function App() {
  // Password access hook
  const { hasAccess, loading: accessLoading, grantAccess } = usePasswordAccess();

  // Supabase data hooks (only load if has access)
  const {
    loading: dataLoading,
    error: dataError,
    goals,
    addGoal,
    updateGoal,
    deleteGoal,
    realMotives,
    addRealMotive,
    deleteRealMotive,
    dailyNotes,
    addDailyNote,
    updateDailyNote,
    deleteDailyNote
  } = useSharedSupabaseData();

  // Multi-image data hooks (only load if has access)
  const {
    loading: imageLoading,
    error: imageError,
    visualizationImages,
    addVisualizationImage,
    deleteVisualizationImage,
    gratitudeImages,
    addGratitudeImage,
    deleteGratitudeImage,
    avoidanceImages,
    addAvoidanceImage,
    deleteAvoidanceImage,
    injusticeImages,
    addInjusticeImage,
    deleteInjusticeImage,
    milestoneImages,
    addMilestoneImage,
    deleteMilestoneImage
  } = useSharedImageData();

  // Show loading spinner while checking access
  if (accessLoading) {
    return <LoadingSpinner />;
  }

  // Show password form if no access
  if (!hasAccess) {
    return <PasswordForm onPasswordCorrect={grantAccess} />;
  }

  // Show error if there's any error
  if (dataError || imageError) {
    return <ErrorMessage error={dataError || imageError} />;
  }

  // Show loading spinner while data is loading
  if (dataLoading || imageLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 font-sans antialiased">
        <div className="max-w-5xl mx-auto p-6 sm:p-10">
          <SharedHeader />
          
          <VideoSection />
          
          <NewVisualizationSection
            images={visualizationImages}
            onAddImage={addVisualizationImage}
            onDeleteImage={deleteVisualizationImage}
          />
          
          <GoalsSection
            goals={goals}
            addGoal={addGoal}
            updateGoal={updateGoal}
            deleteGoal={deleteGoal}
          />
          
          <NewGratitudeSection
            images={gratitudeImages}
            onAddImage={addGratitudeImage}
            onDeleteImage={deleteGratitudeImage}
          />
          
          <NewAvoidanceSection
            images={avoidanceImages}
            onAddImage={addAvoidanceImage}
            onDeleteImage={deleteAvoidanceImage}
          />
          
          <NewInjusticeSection
            images={injusticeImages}
            onAddImage={addInjusticeImage}
            onDeleteImage={deleteInjusticeImage}
          />
          
          <NewMilestoneSection
            images={milestoneImages}
            onAddImage={addMilestoneImage}
            onDeleteImage={deleteMilestoneImage}
          />
          
          <RealMotivesSection
            motives={realMotives}
            addMotive={addRealMotive}
            deleteMotive={deleteRealMotive}
          />
          
          <DailyNotesSection
            notes={dailyNotes}
            addNote={addDailyNote}
            updateNote={updateDailyNote}
            deleteNote={deleteDailyNote}
          />
        </div>
      </div>
    </Router>
  );
}

export default App;